import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Feather, ArrowRight, Mic, Sparkles, Users, BookOpen } from 'lucide-react';

/* ─── Scroll-triggered fade + rise animation ──────────────────────────────── */
function FadeIn({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.07, rootMargin: '0px 0px -48px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(22px)',
        transition: `opacity 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

/* ─── Ornamental section divider ──────────────────────────────────────────── */
function Ornament() {
  return (
    <div className="flex items-center justify-center gap-4 py-4">
      <div className="w-24 h-px bg-[#DDD5C8] dark:bg-[#2A2520]" />
      <Feather className="w-3.5 h-3.5 text-[#C4B0D0] dark:text-[#5C4D6B]" style={{ opacity: 0.5 }} />
      <div className="w-24 h-px bg-[#DDD5C8] dark:bg-[#2A2520]" />
    </div>
  );
}

/* ─── Main component ───────────────────────────────────────────────────────── */
function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8F5F0] dark:bg-[#171411] overflow-x-hidden">

      {/* ── Navigation ──────────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#F8F5F0]/88 dark:bg-[#171411]/88 backdrop-blur-xl border-b border-[#E8E0D4]/50 dark:border-[#2A2520]/60">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Feather className="w-7 h-7 text-[#6B5A7B] dark:text-[#C4B0D0]" />
            <span className="font-serif text-base text-[#3D3530] dark:text-[#E8DFD4] tracking-wide">The Storyteller</span>
          </div>
          <div className="flex items-center gap-6">
            <a
              href="/privacy"
              className="hidden md:inline text-sm text-[#8C847A] hover:text-[#3D3530] dark:hover:text-[#E8DFD4] transition-colors duration-200"
            >
              Privacy
            </a>
            <a
              href="/terms"
              className="hidden md:inline text-sm text-[#8C847A] hover:text-[#3D3530] dark:hover:text-[#E8DFD4] transition-colors duration-200"
            >
              Terms
            </a>
            <button
              onClick={() => navigate('/login')}
              className="text-sm font-medium text-[#5C4D6B] dark:text-[#B0A0C0] px-4 py-1.5 rounded-full border border-[#D8CFC4] dark:border-[#3A3028] hover:border-[#5C4D6B]/50 dark:hover:border-[#B0A0C0]/40 hover:text-[#3D3530] dark:hover:text-[#E8DFD4] transition-all duration-200"
            >
              Sign in
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section className="relative pt-40 sm:pt-56 pb-32 sm:pb-44 px-6">
        {/* Ambient light blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div
            className="absolute top-20 right-[10%] w-[560px] h-[560px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(196,176,208,0.12) 0%, transparent 70%)', filter: 'blur(40px)' }}
          />
          <div
            className="absolute bottom-12 left-[6%] w-[440px] h-[440px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(212,196,160,0.10) 0%, transparent 70%)', filter: 'blur(40px)' }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto text-center">
          {/* Eyebrow */}
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-10"
            style={{
              background: 'rgba(92,77,107,0.07)',
              border: '1px solid rgba(92,77,107,0.13)',
            }}
          >
            <Feather className="w-3 h-3 text-[#5C4D6B] dark:text-[#B0A0C0]" />
            <span className="text-[10px] font-medium tracking-[0.2em] uppercase text-[#5C4D6B] dark:text-[#B0A0C0]">
              The Storyteller
            </span>
          </div>

          {/* Headline */}
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold text-[#2A2420] dark:text-[#F0EBE4] leading-[1.08] tracking-tight mb-9">
            Your life holds more stories<br className="hidden sm:block" />{' '}
            <span className="italic text-[#6B5A7B] dark:text-[#C0B0D4]">than you know.</span>
          </h1>

          {/* Sub */}
          <p className="text-lg sm:text-xl text-[#6B6560] dark:text-[#9C9590] leading-relaxed max-w-2xl mx-auto mb-14">
            We'll help you find them, shape them, and turn them into something you can share with the people you love. No writing experience required.
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
            <button
              onClick={() => navigate('/login')}
              data-testid="landing-cta"
              className="group flex items-center gap-2.5 bg-[#4f415d] hover:bg-[#43374f] dark:bg-[#8a789d] dark:hover:bg-[#796889] text-white px-11 py-4 rounded-full text-base font-medium transition-all duration-300 hover:-translate-y-0.5"
              style={{
                boxShadow: '0 14px 44px -12px rgba(79,65,93,0.42)',
              }}
            >
              Begin Your Story
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
            </button>
            <span className="text-sm text-[#A09890] dark:text-[#5A5248]">
              Free to start &middot; No experience needed
            </span>
          </div>
        </div>
      </section>

      {/* ── Why Stories Matter ──────────────────────────────────────────────── */}
      <section className="py-24 sm:py-36 px-6 bg-[#F0EBE4]/45 dark:bg-[#1E1C18]/45">
        <div className="max-w-3xl mx-auto text-center">
          <FadeIn>
            <p className="text-[10px] font-medium tracking-[0.22em] uppercase text-[#8C847A] dark:text-[#5A5248] mb-6">
              Why it matters
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#2A2420] dark:text-[#F0EBE4] leading-[1.15] tracking-tight mb-16">
              Every family has a book<br />worth writing.
            </h2>
          </FadeIn>

          <div className="grid sm:grid-cols-3 gap-12 text-left">
            <FadeIn delay={80}>
              <div>
                <div className="w-6 h-px bg-[#5C4D6B] dark:bg-[#8a789d] mb-6" />
                <p className="font-serif text-lg text-[#3D3530] dark:text-[#D0C8BC] leading-[1.75] italic">
                  "The stories we tell our children become the stories they tell theirs."
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={180}>
              <div>
                <div className="w-6 h-px bg-[#8b7355] dark:bg-[#c4a882] mb-6" />
                <p className="font-serif text-lg text-[#3D3530] dark:text-[#D0C8BC] leading-[1.75] italic">
                  "A recipe. A memory. A moment that changed everything. These are the things worth keeping."
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={280}>
              <div>
                <div className="w-6 h-px bg-[#5C4D6B] dark:bg-[#8a789d] mb-6" />
                <p className="font-serif text-lg text-[#3D3530] dark:text-[#D0C8BC] leading-[1.75] italic">
                  "You don't need to be a writer. You just need to want to share."
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <Ornament />

      {/* ── Who This Is For ─────────────────────────────────────────────────── */}
      <section className="py-24 sm:py-36 px-6">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="text-center mb-20">
              <p className="text-[10px] font-medium tracking-[0.22em] uppercase text-[#8C847A] dark:text-[#5A5248] mb-4">
                Made for real people
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#2A2420] dark:text-[#F0EBE4] leading-[1.15] tracking-tight">
                This was built for you.
              </h2>
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-2 gap-5">
            <PersonaCard
              delay={0}
              label="Parents & Grandparents"
              quote="Your grandchildren will ask questions one day. Now you'll have answers — and stories — to give them."
            />
            <PersonaCard
              delay={100}
              label="People with meaningful experiences"
              quote="You don't have to explain why it mattered. Just tell the story. The meaning speaks for itself."
            />
            <PersonaCard
              delay={200}
              label="Immigrants & those who've traveled far"
              quote="The journey that brought you here is worth more than you know. Every mile. Every decision. Every beginning."
            />
            <PersonaCard
              delay={300}
              label="People who always wanted to write"
              quote="The only thing standing between you and your book was a place to begin. This is that place."
            />
          </div>

          <FadeIn delay={420}>
            <p className="text-center text-sm text-[#A09890] dark:text-[#5A5248] italic mt-12">
              And anyone else who has lived a life worth sharing.
            </p>
          </FadeIn>
        </div>
      </section>

      <Ornament />

      {/* ── How It Works ────────────────────────────────────────────────────── */}
      <section className="py-24 sm:py-36 px-6 bg-[#F0EBE4]/45 dark:bg-[#1E1C18]/45">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="text-center mb-20">
              <p className="text-[10px] font-medium tracking-[0.22em] uppercase text-[#8C847A] dark:text-[#5A5248] mb-4">
                How it works
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#2A2420] dark:text-[#F0EBE4] leading-[1.15] tracking-tight">
                Three steps.<br />One book.
              </h2>
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-3 gap-12 sm:gap-8">
            <StepCard
              delay={0}
              numeral="I"
              title="Create your book"
              description="Choose what you want to write — a life story, a memoir, a family history, a novel. Your book, your way."
            />
            <StepCard
              delay={150}
              numeral="II"
              title="Have a conversation"
              description="An AI interviewer asks gentle, thoughtful questions. You share what comes naturally. No blank page. No pressure."
            />
            <StepCard
              delay={300}
              numeral="III"
              title="Read your chapter"
              description="Your answers become a written chapter — in your voice, with your stories, shaped into something beautiful."
            />
          </div>
        </div>
      </section>

      {/* ── Interview Mode ───────────────────────────────────────────────────── */}
      <section className="py-24 sm:py-36 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            <FadeIn>
              <div>
                <p className="text-[10px] font-medium tracking-[0.22em] uppercase text-[#8C847A] dark:text-[#5A5248] mb-5">
                  Interview Mode
                </p>
                <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#2A2420] dark:text-[#F0EBE4] leading-[1.15] tracking-tight mb-7">
                  Most people don't know where to start.
                </h2>
                <p className="text-base text-[#6B6560] dark:text-[#9C9590] leading-[1.8] mb-5">
                  That's why we created Interview Mode. Instead of staring at a blank page, you have a conversation.
                </p>
                <p className="text-base text-[#6B6560] dark:text-[#9C9590] leading-[1.8]">
                  Our AI asks the questions. You share what comes naturally — by typing or by speaking aloud. When the conversation is done, your chapter is ready.
                </p>
              </div>
            </FadeIn>

            {/* Chat illustration */}
            <FadeIn delay={150}>
              <div
                className="bg-white/65 dark:bg-[#22201C]/70 rounded-2xl p-7 border border-[#E0D8CC]/60 dark:border-[#2A2520]/60"
                style={{ boxShadow: '0 24px 72px -24px rgba(58,43,31,0.11)' }}
              >
                <ChatBubble
                  role="ai"
                  text="Tell me about where you grew up. What's the first memory that comes to mind?"
                />
                <ChatBubble
                  role="user"
                  text="We lived in a small apartment above my grandfather's shop. I can still smell the bread from the bakery next door every morning."
                />
                <ChatBubble
                  role="ai"
                  text="That's a beautiful detail. What was it like, waking up to that every day?"
                />
                <div className="flex items-center gap-3 mt-5 pt-5 border-t border-[#E0D8CC]/40 dark:border-[#2A2520]/50">
                  <Mic className="w-4 h-4 text-[#5C4D6B] dark:text-[#B0A0C0]" style={{ opacity: 0.55 }} />
                  <div className="flex-1 h-9 bg-[#F0EBE4]/80 dark:bg-[#1A1814]/80 rounded-full px-4 flex items-center border border-[#E0D8CC]/50 dark:border-[#2A2520]/50">
                    <span className="text-xs text-[#A09890] dark:text-[#5A5248] italic">Type or speak your answer...</span>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <Ornament />

      {/* ── Writing Canvas ──────────────────────────────────────────────────── */}
      <section className="py-24 sm:py-36 px-6 bg-[#F0EBE4]/45 dark:bg-[#1E1C18]/45">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            {/* Editor illustration */}
            <FadeIn>
              <div
                className="bg-white/75 dark:bg-[#22201C]/70 rounded-2xl border border-[#E0D8CC]/60 dark:border-[#2A2520]/60 overflow-hidden"
                style={{ boxShadow: '0 24px 72px -24px rgba(58,43,31,0.10)' }}
              >
                {/* Faux window bar */}
                <div className="flex items-center gap-1.5 px-5 py-3.5 border-b border-[#E8E0D4]/50 dark:border-[#2A2520]/60 bg-[#F8F5F0]/80 dark:bg-[#1A1814]/60">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#E0D8CC] dark:bg-[#3A3028]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#E0D8CC] dark:bg-[#3A3028]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#E0D8CC] dark:bg-[#3A3028]" />
                  <span className="text-xs text-[#A09890] dark:text-[#5A5248] ml-2">Chapter One — The Beginning</span>
                </div>
                {/* Fake text content */}
                <div className="p-8 space-y-4">
                  <p className="font-serif text-base font-bold text-[#2A2420] dark:text-[#F0EBE4]">Chapter One</p>
                  <p className="font-serif text-sm text-[#3D3530] dark:text-[#C8C0B4] leading-[1.95]">
                    The apartment smelled of bread and possibility. Every morning began the same way — the warm rush of yeast and flour drifting up from the bakery below, pulling me from sleep before the alarm ever had a chance.
                  </p>
                  <p className="font-serif text-sm text-[#3D3530] dark:text-[#C8C0B4] leading-[1.95]">
                    My grandfather had chosen that location deliberately, he told me years later. He wanted us to wake up to something beautiful.
                  </p>
                  <div className="flex items-center gap-2 pt-2">
                    <div className="w-14 h-px bg-[#E0D8CC] dark:bg-[#2A2520]" />
                    <Sparkles className="w-3 h-3 text-[#5C4D6B] dark:text-[#B0A0C0]" style={{ opacity: 0.35 }} />
                  </div>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={150}>
              <div>
                <p className="text-[10px] font-medium tracking-[0.22em] uppercase text-[#8C847A] dark:text-[#5A5248] mb-5">
                  Writing Canvas
                </p>
                <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#2A2420] dark:text-[#F0EBE4] leading-[1.15] tracking-tight mb-7">
                  When you're ready to write, we're ready to help.
                </h2>
                <p className="text-base text-[#6B6560] dark:text-[#9C9590] leading-[1.8] mb-5">
                  A clean, quiet writing space. AI suggestions when you want them. Voice dictation when typing feels like too much.
                </p>
                <p className="text-base text-[#6B6560] dark:text-[#9C9590] leading-[1.8]">
                  You stay in control. The AI stays in the background until you call on it.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── Your Story Takes Shape ──────────────────────────────────────────── */}
      <section className="py-24 sm:py-36 px-6">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-[10px] font-medium tracking-[0.22em] uppercase text-[#8C847A] dark:text-[#5A5248] mb-4">
                Every tool you need
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#2A2420] dark:text-[#F0EBE4] leading-[1.15] tracking-tight mb-7">
                Watch your story come together.
              </h2>
              <p className="text-lg text-[#6B6560] dark:text-[#9C9590] leading-relaxed max-w-2xl mx-auto">
                Each chapter builds on the last. A built-in writing guide keeps your narrative on track.
              </p>
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-3 gap-6">
            <FeatureCard
              delay={0}
              numeral="01"
              title="Guided structure"
              description="A chapter-by-chapter writing guide shows what you've covered and what's still worth exploring."
            />
            <FeatureCard
              delay={120}
              title="AI writing polish"
              numeral="02"
              description="When a chapter feels right, the AI can help refine the prose — while keeping your voice exactly as it is."
            />
            <FeatureCard
              delay={240}
              title="Voice input"
              numeral="03"
              description="Speak your memories aloud. Your words are transcribed in real time and ready to become your next chapter."
            />
          </div>
        </div>
      </section>

      <Ornament />

      {/* ── What Kind of Book ────────────────────────────────────────────────── */}
      <section className="py-24 sm:py-36 px-6 bg-[#F0EBE4]/45 dark:bg-[#1E1C18]/45">
        <div className="max-w-4xl mx-auto">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-[10px] font-medium tracking-[0.22em] uppercase text-[#8C847A] dark:text-[#5A5248] mb-4">
                Every story has a form
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#2A2420] dark:text-[#F0EBE4] leading-[1.15] tracking-tight">
                What kind of book<br />will you write?
              </h2>
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <BookTypeCard
              delay={0}
              title="Life Story"
              sub="Your whole life. Your words. Your legacy."
            />
            <BookTypeCard
              delay={80}
              title="Family Memoir"
              sub="The people, places, and moments that shaped who you are."
            />
            <BookTypeCard
              delay={160}
              title="Family History"
              sub="Where you came from, told for those who come after."
            />
            <BookTypeCard
              delay={240}
              title="Business Story"
              sub="The honest account of building something from nothing."
            />
            <BookTypeCard
              delay={320}
              title="Novel or Fiction"
              sub="Characters, worlds, and stories drawn from imagination."
            />
            <BookTypeCard
              delay={400}
              title="Children's Story"
              sub="A story written for the smallest readers in your life."
            />
          </div>
        </div>
      </section>

      {/* ── Sharing & Collaboration ──────────────────────────────────────────── */}
      <section className="py-24 sm:py-36 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            <FadeIn>
              <div>
                <p className="text-[10px] font-medium tracking-[0.22em] uppercase text-[#8C847A] dark:text-[#5A5248] mb-5">
                  Sharing & Collaboration
                </p>
                <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#2A2420] dark:text-[#F0EBE4] leading-[1.15] tracking-tight mb-7">
                  Stories are meant to be shared.
                </h2>
                <p className="text-base text-[#6B6560] dark:text-[#9C9590] leading-[1.8] mb-5">
                  When your book is ready, share it with the people who matter. Export a beautifully formatted document. Invite family members to add their own memories.
                </p>
                <p className="text-base text-[#6B6560] dark:text-[#9C9590] leading-[1.8]">
                  The stories you write today become the treasures your family keeps for generations.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={150}>
              <div className="space-y-4">
                <ShareRow icon={Users} label="Invite family members to contribute their memories" />
                <ShareRow icon={BookOpen} label="Export as a beautifully formatted document" />
                <ShareRow icon={Feather} label="Keep your book private, or share it with the world" />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────────────── */}
      <section className="py-36 sm:py-52 px-6 relative">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(196,176,208,0.09) 0%, transparent 65%)', filter: 'blur(20px)' }}
          />
        </div>
        <div className="relative max-w-2xl mx-auto text-center">
          <FadeIn>
            <Feather
              className="w-9 h-9 text-[#C4B0D0] dark:text-[#5C4D6B] mx-auto mb-12"
              style={{ opacity: 0.5 }}
            />
            <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold text-[#2A2420] dark:text-[#F0EBE4] leading-[1.08] tracking-tight mb-8">
              Your story<br />
              <span className="italic text-[#6B5A7B] dark:text-[#C0B0D4]">is waiting.</span>
            </h2>
            <p className="text-xl text-[#6B6560] dark:text-[#9C9590] leading-relaxed mb-14">
              Begin today. The words will come.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="group inline-flex items-center gap-2.5 bg-[#4f415d] hover:bg-[#43374f] dark:bg-[#8a789d] dark:hover:bg-[#796889] text-white px-12 py-4 rounded-full text-base font-medium transition-all duration-300 hover:-translate-y-0.5"
              style={{ boxShadow: '0 16px 50px -12px rgba(79,65,93,0.44)' }}
            >
              Start Writing
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
            </button>
            <p className="text-sm text-[#B0A8A0] dark:text-[#4A4440] mt-7">
              Free to begin &middot; No experience needed
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <footer className="border-t border-[#E0D8CC] dark:border-[#2A2520] py-12 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <Feather className="w-6 h-6 text-[#6B5A7B] dark:text-[#C4B0D0]" />
            <span className="font-serif text-sm text-[#3D3530] dark:text-[#E8DFD4]">The Storyteller</span>
          </div>
          <div className="flex items-center gap-8 text-sm text-[#8C847A]">
            <a href="/privacy" className="hover:text-[#3D3530] dark:hover:text-[#E8DFD4] transition-colors duration-200">Privacy Policy</a>
            <a href="/terms" className="hover:text-[#3D3530] dark:hover:text-[#E8DFD4] transition-colors duration-200">Terms of Service</a>
            <a href="/login" className="hover:text-[#3D3530] dark:hover:text-[#E8DFD4] transition-colors duration-200">Sign in</a>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-8 pt-6 border-t border-[#E0D8CC]/40 dark:border-[#2A2520]/40 text-center text-xs text-[#B0A8A0] dark:text-[#4A4440]">
          &copy; {new Date().getFullYear()} The Storyteller &middot; Every life holds stories worth sharing.
        </div>
      </footer>
    </div>
  );
}

/* ─── Sub-components ───────────────────────────────────────────────────────── */

function PersonaCard({ label, quote, delay = 0 }) {
  return (
    <FadeIn delay={delay}>
      <div className="p-7 sm:p-9 bg-white/55 dark:bg-[#22201C]/55 rounded-2xl border border-[#E0D8CC]/55 dark:border-[#2A2520]/60 hover:border-[#C4B0D0]/55 dark:hover:border-[#5C4D6B]/45 hover:shadow-[0_14px_44px_-14px_rgba(58,43,31,0.09)] transition-all duration-300">
        <p className="text-[10px] font-medium tracking-[0.18em] uppercase text-[#5C4D6B] dark:text-[#8a789d] mb-5">
          {label}
        </p>
        <p className="font-serif text-lg text-[#3D3530] dark:text-[#C8C0B4] leading-[1.75] italic">
          &ldquo;{quote}&rdquo;
        </p>
      </div>
    </FadeIn>
  );
}

function StepCard({ numeral, title, description, delay = 0 }) {
  return (
    <FadeIn delay={delay}>
      <div>
        <div
          className="font-serif text-5xl text-[#C4B0D0] dark:text-[#3A3028] italic mb-6 leading-none"
          aria-hidden="true"
        >
          {numeral}
        </div>
        <h3 className="font-serif text-xl font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-3">{title}</h3>
        <p className="text-sm text-[#7C766E] dark:text-[#8C847A] leading-[1.8]">{description}</p>
      </div>
    </FadeIn>
  );
}

function FeatureCard({ numeral, title, description, delay = 0 }) {
  return (
    <FadeIn delay={delay}>
      <div className="p-7 bg-white/60 dark:bg-[#22201C]/60 rounded-2xl border border-[#E0D8CC]/55 dark:border-[#2A2520]/55 hover:border-[#C4B0D0]/55 dark:hover:border-[#5C4D6B]/45 transition-all duration-300">
        <span className="text-[10px] font-medium text-[#C4B8A8] dark:text-[#4A4440] tracking-[0.15em]">{numeral}</span>
        <h3 className="font-serif text-lg font-bold text-[#2A2420] dark:text-[#F0EBE4] mt-4 mb-3">{title}</h3>
        <p className="text-sm text-[#7C766E] dark:text-[#8C847A] leading-[1.8]">{description}</p>
      </div>
    </FadeIn>
  );
}

function BookTypeCard({ title, sub, delay = 0 }) {
  return (
    <FadeIn delay={delay}>
      <div className="p-5 sm:p-6 bg-white/55 dark:bg-[#22201C]/55 rounded-xl border border-[#E0D8CC]/55 dark:border-[#2A2520]/55 hover:border-[#C4B0D0]/55 dark:hover:border-[#5C4D6B]/45 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-10px_rgba(58,43,31,0.08)] transition-all duration-300">
        <p className="font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-1.5">{title}</p>
        <p className="text-sm text-[#8C847A] dark:text-[#6A6058] leading-relaxed">{sub}</p>
      </div>
    </FadeIn>
  );
}

function ChatBubble({ role, text }) {
  const isAi = role === 'ai';
  return (
    <div className={`flex mb-3 ${isAi ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`max-w-[85%] px-4 py-3 text-sm leading-[1.7] ${
          isAi
            ? 'bg-[#F0EBE4]/90 dark:bg-[#2A2520]/70 text-[#3D3530] dark:text-[#C8C0B4] rounded-2xl rounded-bl-sm'
            : 'bg-[#4f415d] dark:bg-[#8a789d] text-white rounded-2xl rounded-br-sm'
        }`}
      >
        {text}
      </div>
    </div>
  );
}

function ShareRow({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-4 p-5 bg-white/55 dark:bg-[#22201C]/55 rounded-xl border border-[#E0D8CC]/55 dark:border-[#2A2520]/55">
      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#4f415d]/08 dark:bg-[#8a789d]/12 flex-shrink-0"
        style={{ background: 'rgba(79,65,93,0.07)' }}
      >
        <Icon className="w-4 h-4 text-[#5C4D6B] dark:text-[#B0A0C0]" />
      </div>
      <p className="text-sm text-[#4A4440] dark:text-[#C0BAB0] leading-relaxed">{label}</p>
    </div>
  );
}

export default LandingPage;
